import type { IBountyService } from "@/services/interfaces/IBountyService";
import type {
  BountyCard,
  BountyDetail,
  BountyStatus,
  DiscoverParams,
  InterestedBounty,
  PaginatedResult,
  WorkSubmission,
} from "@/lib/types";
import { deriveQuestScene } from "@/lib/questArt/scene";

const MOCK_DELAY_MS = 800;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Fixtures without `artScene`, which is DERIVED below rather than written out
 * seventeen times. Deriving it keeps the mock in step with the real adapter
 * automatically: both resolve the scene from the same category and skills, so
 * a change to the derivation cannot leave these fixtures asserting art the
 * production path would never produce.
 */
const MOCK_BOUNTY_CARD_DATA: Omit<BountyCard, "artScene">[] = [
  {
    id: "mock-bounty-001",
    taskTitle: "Build responsive navigation component",
    taskDescription:
      "Create a mobile-first responsive navigation bar with hamburger menu, search input, and profile dropdown. Must follow the existing design system.",
    taskType: "human",
    questTitle: "Build a Landing Page",
    questId: "mock-quest-001",
    category: "Development",
    budgetUSD: 120,
    currency: "USDT",
    estimatedEffortHours: 4,
    deadline: new Date(Date.now() + 7 * 86400000).toISOString(),
    skills: ["React", "TypeScript", "Tailwind CSS"],
    creatorName: "Alice Creator",
    creatorBlurb: "Design studio, four staff, Valencia",
    creatorAvatarUrl: null,
    // Exercises the "posted as an org" poster line (organizations PR 2).
    postedAsOrg: { slug: "fundacion-luque-verde", name: "Fundación Luque Verde", logoUrl: null, verified: true },
    creatorId: "mock-creator-alice-creator",
    // DEV-824: one fixture carries real translations so the mock adapter can
    // exercise the language toggle and the "Translated · Show original"
    // affordance without a Supabase stack behind it.
    i18n: {
      es: {
        title: "Construir un componente de navegación adaptable",
        description:
          "Crea una barra de navegación adaptable, pensada primero para móvil, con menú hamburguesa, campo de búsqueda y desplegable de perfil. Debe seguir el sistema de diseño existente.",
      },
      pt: {
        title: "Criar um componente de navegação responsivo",
        description:
          "Crie uma barra de navegação responsiva, pensada primeiro para o celular, com menu hambúrguer, campo de busca e menu suspenso de perfil. Deve seguir o sistema de design existente.",
      },
    },
    sourceLang: "en",
    questI18n: {
      es: { title: "Crear una página de destino" },
      pt: { title: "Criar uma página de destino" },
    },
    questSourceLang: "en",
    applicantCount: 3,
  },
  {
    id: "mock-bounty-002",
    taskTitle: "Write API integration tests",
    taskDescription:
      "Write comprehensive integration tests for the REST API endpoints covering auth, quests, and bounty flows. Use Vitest and MSW for mocking.",
    taskType: "human",
    questTitle: "Backend API Hardening",
    questId: "mock-quest-010",
    category: "Development",
    budgetUSD: 200,
    currency: "USDT",
    estimatedEffortHours: 8,
    deadline: new Date(Date.now() + 14 * 86400000).toISOString(),
    skills: ["Testing", "TypeScript", "Vitest"],
    creatorName: "Bob Builder",
    creatorBlurb: "Independent contractor, Barcelona",
    creatorAvatarUrl: null,
    creatorId: "mock-creator-bob-builder-new",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 1,
  },
  {
    id: "mock-bounty-003",
    taskTitle: "Design onboarding illustrations",
    taskDescription:
      "Create 4 custom illustrations for the onboarding flow. Style: flat, modern, tech-themed. Deliverables: SVG and PNG at 2x resolution.",
    taskType: "human",
    questTitle: "User Onboarding Redesign",
    questId: "mock-quest-011",
    category: "Design",
    budgetUSD: 350,
    currency: "USDT",
    estimatedEffortHours: 10,
    deadline: new Date(Date.now() + 21 * 86400000).toISOString(),
    skills: ["Illustration", "SVG", "UI Design"],
    creatorName: "Carol Designer",
    creatorBlurb: "Freelance product designer, Lisbon",
    creatorAvatarUrl: null,
    creatorId: "mock-creator-carol-designer",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 5,
  },
  {
    id: "mock-bounty-004",
    taskTitle: "Generate API documentation from OpenAPI spec",
    taskDescription:
      "Parse the OpenAPI 3.0 spec file and produce well-formatted markdown documentation for each endpoint, including request/response examples.",
    taskType: "ai",
    questTitle: "Developer Portal",
    questId: "mock-quest-012",
    category: "Writing",
    budgetUSD: 50,
    currency: "USDT",
    estimatedEffortHours: 2,
    deadline: null,
    skills: ["Technical Writing", "OpenAPI"],
    creatorName: "Dave Docs",
    creatorBlurb: "Technical writing consultancy, remote",
    creatorAvatarUrl: null,
    creatorId: "mock-creator-dave-docs",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 0,
  },
  {
    id: "mock-bounty-005",
    taskTitle: "Smart contract security audit",
    taskDescription:
      "Perform a security review of the escrow smart contract. Identify vulnerabilities, gas optimization opportunities, and recommend fixes.",
    taskType: "human",
    questTitle: "Polygon Integration",
    questId: "mock-quest-013",
    category: "Development",
    budgetUSD: 500,
    currency: "USDT",
    estimatedEffortHours: 16,
    deadline: new Date(Date.now() + 10 * 86400000).toISOString(),
    skills: ["Solidity", "Security", "Ethereum"],
    creatorName: "Eve Auditor",
    creatorBlurb: "Smart-contract audit firm, six staff",
    creatorAvatarUrl: null,
    creatorId: "mock-creator-eve-auditor",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 2,
  },
  {
    id: "mock-bounty-006",
    taskTitle: "Build email notification system",
    taskDescription:
      "Implement transactional email notifications using Resend. Notifications for quest creation, bounty acceptance, submission reviews, and payment releases.",
    taskType: "human",
    questTitle: "Notification System",
    questId: "mock-quest-014",
    category: "Development",
    budgetUSD: 280,
    currency: "USDT",
    estimatedEffortHours: 6,
    deadline: new Date(Date.now() + 12 * 86400000).toISOString(),
    skills: ["TypeScript", "React", "Email"],
    creatorName: "Frank Ops",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-frank-ops",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 2,
  },
  {
    id: "mock-bounty-007",
    taskTitle: "Write SEO-optimized blog posts",
    taskDescription:
      "Write 5 blog posts about decentralized work, each 1500-2000 words, targeting specific keywords. Include meta descriptions and alt text.",
    taskType: "human",
    questTitle: "Content Marketing Launch",
    questId: "mock-quest-015",
    category: "Writing",
    budgetUSD: 400,
    currency: "USDT",
    estimatedEffortHours: 20,
    deadline: new Date(Date.now() + 30 * 86400000).toISOString(),
    skills: ["Writing", "SEO", "Marketing"],
    creatorName: "Grace Writer",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-grace-writer",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 4,
  },
  {
    id: "mock-bounty-008",
    taskTitle: "Analyze user engagement metrics",
    taskDescription:
      "Set up analytics dashboards and produce a report on user engagement, retention, and conversion funnels. Recommend improvements based on data.",
    taskType: "human",
    questTitle: "Growth Analytics",
    questId: "mock-quest-016",
    category: "Data",
    budgetUSD: 300,
    currency: "USDT",
    estimatedEffortHours: 12,
    deadline: new Date(Date.now() + 18 * 86400000).toISOString(),
    skills: ["Data Analysis", "Python", "SQL"],
    creatorName: "Hank Analyst",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-hank-analyst",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 1,
  },
  {
    id: "mock-bounty-009",
    taskTitle: "Create social media graphics pack",
    taskDescription:
      "Design 20 social media graphics for Twitter, LinkedIn, and Instagram. Follow brand guidelines. Deliver in Figma and PNG formats.",
    taskType: "human",
    questTitle: "Brand Refresh Campaign",
    questId: "mock-quest-017",
    category: "Design",
    budgetUSD: 250,
    currency: "USDT",
    estimatedEffortHours: 8,
    deadline: new Date(Date.now() + 9 * 86400000).toISOString(),
    skills: ["UI Design", "Figma", "Marketing"],
    creatorName: "Iris Creative",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-iris-creative",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 6,
  },
  {
    id: "mock-bounty-010",
    taskTitle: "Summarize research papers on DeFi governance",
    taskDescription:
      "Read and summarize 10 academic papers on decentralized governance models. Produce a structured summary with key findings and implications.",
    taskType: "ai",
    questTitle: "Governance Research",
    questId: "mock-quest-018",
    category: "Research",
    budgetUSD: 80,
    currency: "USDT",
    estimatedEffortHours: 4,
    deadline: null,
    skills: ["Research", "Writing", "DeFi"],
    creatorName: "Jack Scholar",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-jack-scholar",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 0,
  },
  {
    id: "mock-bounty-011",
    taskTitle: "Build a Python data pipeline",
    taskDescription:
      "Create an ETL pipeline that pulls data from multiple APIs, transforms it, and loads into a PostgreSQL database. Include error handling and logging.",
    taskType: "human",
    questTitle: "Data Infrastructure",
    questId: "mock-quest-019",
    category: "Data",
    budgetUSD: 450,
    currency: "USDT",
    estimatedEffortHours: 14,
    deadline: new Date(Date.now() + 20 * 86400000).toISOString(),
    skills: ["Python", "SQL", "Data Analysis"],
    creatorName: "Kate Engineer",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-kate-engineer",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 3,
  },
  {
    id: "mock-bounty-012",
    taskTitle: "Create online course curriculum",
    taskDescription:
      "Design a 6-module online course on smart contract development. Include learning objectives, exercises, and assessments for each module.",
    taskType: "human",
    questTitle: "Educational Platform",
    questId: "mock-quest-020",
    category: "Learning",
    budgetUSD: 600,
    currency: "USDT",
    estimatedEffortHours: 24,
    deadline: new Date(Date.now() + 45 * 86400000).toISOString(),
    skills: ["Technical Writing", "Solidity", "Education"],
    creatorName: "Leo Teacher",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-leo-teacher",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 2,
  },
  {
    id: "mock-bounty-013",
    taskTitle: "Set up CI/CD pipeline",
    taskDescription:
      "Configure GitHub Actions for automated testing, linting, and deployment. Include staging and production environments with preview deployments.",
    taskType: "human",
    questTitle: "DevOps Foundation",
    questId: "mock-quest-021",
    category: "Development",
    budgetUSD: 180,
    currency: "USDT",
    estimatedEffortHours: 6,
    deadline: new Date(Date.now() + 5 * 86400000).toISOString(),
    skills: ["DevOps", "GitHub Actions", "TypeScript"],
    creatorName: "Mia DevOps",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-mia-devops",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 1,
  },
  {
    id: "mock-bounty-014",
    taskTitle: "Translate app to French",
    taskDescription:
      "Translate all user-facing strings from English to French. Maintain tone and context. Review with a native speaker.",
    taskType: "human",
    questTitle: "Internationalization Phase 2",
    questId: "mock-quest-022",
    category: "Writing",
    budgetUSD: 150,
    currency: "USDT",
    estimatedEffortHours: 8,
    deadline: new Date(Date.now() + 15 * 86400000).toISOString(),
    skills: ["Writing", "Translation"],
    creatorName: "Nina Translator",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-nina-translator",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 3,
  },
  {
    id: "mock-bounty-015",
    taskTitle: "Auto-classify support tickets",
    taskDescription:
      "Build an AI classifier that categorizes incoming support tickets by priority and department. Use existing labeled data for training.",
    taskType: "ai",
    questTitle: "Support Automation",
    questId: "mock-quest-023",
    category: "Data",
    budgetUSD: 100,
    currency: "USDT",
    estimatedEffortHours: 5,
    deadline: new Date(Date.now() + 8 * 86400000).toISOString(),
    skills: ["Python", "Machine Learning", "Data Analysis"],
    creatorName: "Oscar ML",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-oscar-ml",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 1,
  },
  {
    id: "mock-bounty-016",
    taskTitle: "Competitive market analysis",
    taskDescription:
      "Research and analyze 10 competitors in the gig economy space. Produce a report with feature comparison, pricing, and market positioning.",
    taskType: "human",
    questTitle: "Market Research Q1",
    questId: "mock-quest-024",
    category: "Research",
    budgetUSD: 350,
    currency: "USDT",
    estimatedEffortHours: 15,
    deadline: new Date(Date.now() + 25 * 86400000).toISOString(),
    skills: ["Research", "Writing", "Marketing"],
    creatorName: "Paula Researcher",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-paula-researcher",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 2,
  },
  {
    id: "mock-bounty-017",
    taskTitle: "Translate community docs to Spanish",
    taskDescription:
      "Translate the community onboarding guide and FAQ from English to Spanish. Maintain tone and formatting. ~2,000 words total.",
    taskType: "human",
    questTitle: "Open Source Localization Drive",
    questId: "mock-quest-025",
    category: "Writing",
    budgetUSD: 0,
    currency: "USDC",
    estimatedEffortHours: 3,
    deadline: new Date(Date.now() + 30 * 86400000).toISOString(),
    skills: ["Spanish", "Writing", "Translation"],
    creatorName: "Community DAO",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-community-dao",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 0,
  },
  {
    id: "mock-bounty-018",
    taskTitle: "Mentor a junior developer",
    taskDescription:
      "Provide 1-on-1 mentorship to a junior developer learning React and TypeScript. Two 1-hour sessions per week for 4 weeks.",
    taskType: "human",
    questTitle: "Developer Mentorship Program",
    questId: "mock-quest-026",
    category: "Learning",
    budgetUSD: 0,
    currency: "USDC",
    estimatedEffortHours: 8,
    deadline: null,
    skills: ["React", "TypeScript", "Mentoring"],
    creatorName: "WeXare Foundation",
    creatorBlurb: null,
    creatorAvatarUrl: null,
    creatorId: "mock-creator-wexare-foundation",
    i18n: {},
    sourceLang: null,
    questI18n: {},
    questSourceLang: null,
    applicantCount: 2,
  },
];

/**
 * DEV-794: posting ages cycled across the fixtures so a mock-mode feed shows
 * every "Posted" wording at once — today, yesterday, a few days, and past the
 * one-week switch to a plain date.
 */
const MOCK_POSTED_DAYS_AGO = [0, 1, 3, 6, 12, 40];

/**
 * Trust-grade spread so the chips, the range and the Special styling all
 * render in mock mode: a range (G2 to G4), some G4, a G3, a G1 and one Special.
 */
const MOCK_TRUST: Record<number, Pick<BountyCard, "trustGrade" | "trustGradeLeast">> = {
  0: { trustGrade: 2, trustGradeLeast: 4 },
  1: { trustGrade: 4, trustGradeLeast: 4 },
  2: { trustGrade: 3, trustGradeLeast: 3 },
  3: { trustGrade: 2, trustGradeLeast: 2 },
  4: { trustGrade: 4, trustGradeLeast: 4 },
  5: { trustGrade: 1, trustGradeLeast: 1 },
  6: { trustGrade: 0, trustGradeLeast: 0 },
  7: { trustGrade: 4, trustGradeLeast: 4 },
};

const MOCK_BOUNTY_CARDS: BountyCard[] = MOCK_BOUNTY_CARD_DATA.map((card, i) => ({
  ...card,
  ...MOCK_TRUST[i],
  artScene: deriveQuestScene(card.category, card.skills),
  questCreatedAt:
    card.questCreatedAt ??
    new Date(Date.now() - MOCK_POSTED_DAYS_AGO[i % MOCK_POSTED_DAYS_AGO.length]! * 86400000).toISOString(),
}));

function buildMockDetails(): Map<string, BountyDetail> {
  return new Map(
    MOCK_BOUNTY_CARDS.map((card) => [
      card.id,
      {
        ...card,
        fullDescription: `${card.taskDescription}\n\nAdditional context: This task is part of the "${card.questTitle}" quest. Please review the requirements carefully before accepting.`,
        attachments: [],
        requirements: [
          "Must follow existing code style and conventions",
          "Include relevant tests for all new code",
          "Submit a brief write-up of approach and trade-offs",
        ],
        parentTaskContext: [],
        status: "open" as BountyStatus,
        assigneeId: null,
      },
    ]),
  );
}

export class MockBountyService implements IBountyService {
  private bountyCards: BountyCard[] = [...MOCK_BOUNTY_CARDS];
  private bountyDetails: Map<string, BountyDetail> = buildMockDetails();
  private swipedRight: Map<string, Set<string>> = new Map();
  private swipedLeft: Map<string, Set<string>> = new Map();
  private submissions: Map<string, WorkSubmission> = new Map();

  async getDiscoverFeed(
    params: DiscoverParams,
  ): Promise<PaginatedResult<BountyCard>> {
    await delay(MOCK_DELAY_MS);

    let feed = [...this.bountyCards];

    // Filter out bounties the user has already swiped on (only when authenticated)
    if (params.userId) {
      const rightSet = this.swipedRight.get(params.userId) ?? new Set();
      const leftSet = this.swipedLeft.get(params.userId) ?? new Set();
      feed = feed.filter(
        (b) => !rightSet.has(b.id) && !leftSet.has(b.id),
      );
    }

    // Apply text search
    if (params.search) {
      const q = params.search.toLowerCase();
      feed = feed.filter(
        (b) =>
          b.taskTitle.toLowerCase().includes(q) ||
          b.taskDescription.toLowerCase().includes(q) ||
          b.skills.some((s) => s.toLowerCase().includes(q)),
      );
    }

    // Apply skill filter
    if (params.skills && params.skills.length > 0) {
      feed = feed.filter((b) =>
        b.skills.some((s) => params.skills!.includes(s)),
      );
    }

    // Apply category filter
    if (params.categories && params.categories.length > 0) {
      feed = feed.filter((b) => params.categories!.includes(b.category));
    }

    // Apply budget filters
    if (params.budgetMin !== undefined) {
      feed = feed.filter((b) => b.budgetUSD >= params.budgetMin!);
    }
    if (params.budgetMax !== undefined) {
      feed = feed.filter((b) => b.budgetUSD <= params.budgetMax!);
    }

    // Apply task type filter
    if (params.taskType) {
      feed = feed.filter((b) => b.taskType === params.taskType);
    }

    // Sort
    const sortBy = params.sortBy ?? "newest";
    feed.sort((a, b) => {
      switch (sortBy) {
        case "budget_desc":
          return b.budgetUSD - a.budgetUSD;
        case "budget_asc":
          return a.budgetUSD - b.budgetUSD;
        case "deadline_asc": {
          if (!a.deadline) return 1;
          if (!b.deadline) return -1;
          return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
        }
        case "newest":
        default:
          return 0; // mock data is already in creation order
      }
    });

    // Cursor-based pagination
    const limit = params.limit ?? 10;
    let startIndex = 0;
    if (params.cursor) {
      const cursorIdx = feed.findIndex((b) => b.id === params.cursor);
      if (cursorIdx !== -1) startIndex = cursorIdx + 1;
    }

    const page = feed.slice(startIndex, startIndex + limit);
    const hasMore = startIndex + limit < feed.length;
    const nextCursor = hasMore ? page[page.length - 1]?.id ?? null : null;

    return {
      data: page,
      total: feed.length,
      cursor: nextCursor,
      hasMore,
    };
  }

  async swipeRight(bountyId: string, userId: string): Promise<void> {
    await delay(300);

    if (!this.swipedRight.has(userId)) {
      this.swipedRight.set(userId, new Set());
    }
    this.swipedRight.get(userId)!.add(bountyId);
  }

  /**
   * Derived from `swipedRight` itself rather than tracked in a second map: the
   * set of right-swipes IS the interest list, and two structures holding the
   * same fact drift. A bounty whose detail the fixture does not hold is
   * skipped rather than faked — the real query inner-joins `tasks`, so a swipe
   * pointing at nothing returns nothing there too.
   */
  async getMyInterests(userId: string): Promise<InterestedBounty[]> {
    await delay(300);

    const swiped = this.swipedRight.get(userId);
    if (!swiped) return [];

    return [...swiped].flatMap((taskId) => {
      const detail = this.bountyDetails.get(taskId);
      if (!detail) return [];
      return [
        {
          taskId: detail.id,
          questId: detail.questId,
          taskTitle: detail.taskTitle,
          questTitle: detail.questTitle,
          i18n: detail.i18n ?? null,
          sourceLang: detail.sourceLang ?? null,
          questI18n: detail.questI18n ?? null,
          questSourceLang: detail.questSourceLang ?? null,
          budgetUsd: detail.budgetUSD,
          currency: detail.currency,
          status: detail.status,
          appliedAt: new Date().toISOString(),
        },
      ];
    });
  }

  async swipeLeft(bountyId: string, userId: string): Promise<void> {
    await delay(300);

    if (!this.swipedLeft.has(userId)) {
      this.swipedLeft.set(userId, new Set());
    }
    this.swipedLeft.get(userId)!.add(bountyId);
  }

  async getBountyDetail(id: string): Promise<BountyDetail> {
    await delay(500);

    const detail = this.bountyDetails.get(id);
    if (!detail) {
      throw new Error(`Bounty not found: ${id}`);
    }
    return { ...detail };
  }

  async acceptBounty(bountyId: string, userId: string): Promise<void> {
    await delay(MOCK_DELAY_MS);

    const detail = this.bountyDetails.get(bountyId);
    if (!detail) {
      throw new Error(`Bounty not found: ${bountyId}`);
    }

    this.bountyDetails.set(bountyId, {
      ...detail,
      status: "assigned",
      assigneeId: userId,
    });
  }

  async submitWork(
    bountyId: string,
    submission: WorkSubmission,
  ): Promise<string> {
    await delay(MOCK_DELAY_MS);

    const detail = this.bountyDetails.get(bountyId);
    if (!detail) {
      throw new Error(`Bounty not found: ${bountyId}`);
    }

    const persistedId = `mock-sub-${Date.now()}`;
    this.submissions.set(persistedId, {
      ...submission,
      id: persistedId,
      status: "pending",
      submittedAt: new Date().toISOString(),
    });

    this.bountyDetails.set(bountyId, {
      ...detail,
      status: "submitted",
    });

    return persistedId;
  }

  async getMyBounties(
    userId: string,
    status?: BountyStatus,
  ): Promise<PaginatedResult<BountyDetail>> {
    await delay(MOCK_DELAY_MS);

    let bounties = Array.from(this.bountyDetails.values()).filter(
      (b) => b.assigneeId === userId,
    );

    if (status) {
      bounties = bounties.filter((b) => b.status === status);
    }

    return {
      data: bounties,
      total: bounties.length,
      cursor: null,
      hasMore: false,
    };
  }

  async getPendingSubmissionId(taskId: string): Promise<string> {
    await delay(MOCK_DELAY_MS);

    const pending = [...this.submissions.values()]
      .filter((s) => s.taskId === taskId && s.status === "pending")
      .sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
    if (!pending[0]) throw new Error("No pending submission found");
    return pending[0].id;
  }

  async reviewSubmission(
    submissionId: string,
    decision: "approve" | "request_changes",
    feedback: string | undefined,
    _expectedQuestId: string,
  ): Promise<void> {
    await delay(MOCK_DELAY_MS);

    const submission = this.submissions.get(submissionId);
    if (!submission) {
      throw new Error(`Submission not found: ${submissionId}`);
    }

    const newStatus =
      decision === "approve" ? "approved" : "changes_requested";

    this.submissions.set(submissionId, {
      ...submission,
      status: newStatus,
      feedback: feedback ?? null,
    });

    // Update bounty status accordingly
    const bountyDetail = this.bountyDetails.get(submission.taskId);
    if (bountyDetail) {
      this.bountyDetails.set(submission.taskId, {
        ...bountyDetail,
        status: decision === "approve" ? "approved" : "in_progress",
      });
    }
  }

  async expressInterestOnChain(
    _taskId: string,
    options?: { onStepChange?: (stepId: string, status: "pending" | "active" | "done" | "error") => void },
  ): Promise<void> {
    options?.onStepChange?.("express_interest", "active");
    await delay(MOCK_DELAY_MS);
    options?.onStepChange?.("express_interest", "done");
  }

  async acceptApplicantOnChain(
    _taskId: string,
    _workerUserId: string,
    options?: { onStepChange?: (stepId: string, status: "pending" | "active" | "done" | "error") => void },
  ): Promise<void> {
    options?.onStepChange?.("accept_applicant", "active");
    await delay(MOCK_DELAY_MS);
    options?.onStepChange?.("accept_applicant", "done");
  }
}
